import axios from "axios";
import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { FaUpload, FaFileAlt, FaCheckCircle } from "react-icons/fa";
import "./FlightUpload.css";

const FlightUpload = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.warning("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/flight/bulk-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success("Flights uploaded successfully");

      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="upload-container">
      <div className="upload-card">
        <div className="upload-header">
          <h2 className="upload-title">Upload Flights Data</h2>
          <p className="upload-subtitle">
            Upload your flight data in CSV, Excel, or JSON format
          </p>
        </div>

        <div
          className={`drop-zone ${dragActive ? "active" : ""} ${
            file ? "has-file" : ""
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.json"
            onChange={handleFileChange}
            className="file-input-hidden"
          />

          {file ? (
            <div className="file-preview">
              <FaFileAlt size={48} className="file-icon" />
              <p className="file-name">{file.name}</p>
              <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
              <FaCheckCircle size={24} className="check-icon" />
            </div>
          ) : (
            <div className="drop-zone-content">
              <FaUpload size={48} className="upload-icon" />
              <p className="drop-zone-text">
                Drag & drop your file here, or <span>click to browse</span>
              </p>
              <p className="drop-zone-hint">
                Supported formats: CSV, Excel or JSON
              </p>
            </div>
          )}
        </div>

        <button
          onClick={handleUpload}
          disabled={loading || !file}
          className={`upload-button ${loading ? "loading" : ""}`}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Uploading...
            </>
          ) : (
            <>
              <FaUpload size={20} />
              Upload File
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FlightUpload;
